import { useMutation, useQueryClient } from "@tanstack/react-query";
import { $api, fetchClient } from "../generated/client";
import type { StorefrontEmbedAuditDto } from "../generated/schema";

const AUDIT_PATH = "/super-admin/shops/{shopId}/storefront-embed";

const auditOptions = (shopId: string) =>
  $api.queryOptions("get", AUDIT_PATH, { params: { path: { shopId } } });

// The server caches the audit 24h, so the browser never needs to refetch it.
export const storefrontEmbedQueryOptions = (
  shopId: string,
  enabled: boolean,
) => ({
  ...auditOptions(shopId),
  enabled,
  staleTime: Infinity,
  // Hundreds of requests at once: a token refresh or a deploy can fail a few of
  // them transiently, and a failed row would otherwise stay failed.
  retry: 2,
});

const messageOf = (error: unknown, fallback: string): string =>
  typeof error === "object" &&
  error !== null &&
  "message" in error &&
  typeof error.message === "string"
    ? error.message
    : fallback;

export function useRefreshSuperAdminStorefrontEmbed(shopId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<StorefrontEmbedAuditDto> => {
      const { data, error } = await fetchClient.GET(AUDIT_PATH, {
        params: { path: { shopId }, query: { refresh: true } },
      });
      if (!data)
        throw new Error(messageOf(error, "Rafraîchissement impossible"));
      return data;
    },
    onSuccess: (audit) =>
      queryClient.setQueryData(auditOptions(shopId).queryKey, audit),
  });
}

// Irreversible: Shopify no longer accepts new script tags.
export function useDeleteSuperAdminBabackScriptTags(shopId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<StorefrontEmbedAuditDto> => {
      const { data, error } = await fetchClient.DELETE(
        "/super-admin/shops/{shopId}/script-tags",
        { params: { path: { shopId } } },
      );
      if (!data) throw new Error(messageOf(error, "Suppression impossible"));
      return data;
    },
    onSuccess: (audit) =>
      queryClient.setQueryData(auditOptions(shopId).queryKey, audit),
  });
}
